"use client";

import { useEffect, useRef, useState } from "react";
import NextImage from "next/image";
import { useTranslations } from "next-intl";

import { analyticsManager } from "@/app/utilities/analytics";
import { imageLoader } from "@/app/utilities/images";
import {
	RecentlyViewedCarousel,
	type RecentlyViewedCarouselProduct
} from "@/composites/product/RecentlyViewedCarousel";
import { getProductsBySkus, type ProductSummary } from "@/app/actions/product";

import {
	PC_ADD_TO_CART_EVENT,
	type AddToCartEventDetail
} from "@/app/components/shared/recommended-products/add-to-cart-event";
import { useRecentlyViewed } from "./use-recently-viewed";
import type { RecentlyViewedProductsProps } from ".";
import * as styles from "./recently-viewed-products.module.scss";

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const DEFAULT_MAX_DISPLAY = 12;

/* ------------------------------------------------------------------ */
/*  Utility: map a ProductSummary to a carousel slide                  */
/* ------------------------------------------------------------------ */

type SlideImageProps = {
	src?: string;
	alt?: string;
	width?: number;
	height?: number;
	sizes?: string;
	className?: string;
	component?: React.ElementType;
	loader?: (input: {
		src: string;
		width: number;
		quality?: number;
	}) => string;
};

function mapToSlide(
	product: ProductSummary
): RecentlyViewedCarouselProduct<SlideImageProps> {
	const image: SlideImageProps = {
		src: product.image || undefined,
		alt: product.name,
		width: 304,
		height: 304,
		...(product.image ? { component: NextImage, loader: imageLoader } : {})
	};

	const ratingCount = product.numRatings ?? 0;
	const ratingScore = product.starsRating ?? 0;
	const finalPrice = product.salePrice ?? 0;
	const originalPrice = product.listPrice ?? finalPrice;

	return {
		sku: product.sku,
		image,
		title: { children: product.name, href: `/shop/${product.sku}` },
		...(ratingCount > 0
			? {
					rating: {
						rating: ratingScore,
						label: `(${ratingCount})`
					}
				}
			: {}),
		...(!product.subscription
			? {
					price: {
						display: finalPrice,
						...(originalPrice !== finalPrice
							? {
									productValue: {
										value: originalPrice,
										label: "value"
									}
								}
							: {})
					}
				}
			: {}),
		isVirtualProduct: product.subscription
	};
}

/** Routes the click through CartEventBridge so Recently Viewed shares the
 *  canonical add-to-cart action and slider. */
function dispatchAddToCart(sku: string): void {
	const detail: AddToCartEventDetail = {
		productCode: sku,
		quantity: "1",
		purchaseType: "oneTime",
		source: "recently-viewed"
	};
	window.dispatchEvent(new CustomEvent(PC_ADD_TO_CART_EVENT, { detail }));
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function RecentlyViewedProductsClient({
	currentSku,
	title,
	maxDisplay = DEFAULT_MAX_DISPLAY,
	embedded,
	allowOverflow
}: RecentlyViewedProductsProps): JSX.Element | null {
	const tCart = useTranslations("cart");
	const { skus } = useRecentlyViewed(currentSku);
	const [products, setProducts] = useState<ProductSummary[]>([]);
	const viewedListKey = useRef<string>("");

	const shownKey = products
		.slice(0, maxDisplay)
		.map((p) => p.sku)
		.join(",");

	useEffect(() => {
		if (!shownKey || viewedListKey.current === shownKey) return;
		viewedListKey.current = shownKey;
		analyticsManager.track({
			type: "view_item_list",
			itemListName: "Recently Viewed",
			skus: shownKey.split(",")
		});
	}, [shownKey]);

	useEffect(() => {
		if (skus.length === 0) return;

		let cancelled = false;

		getProductsBySkus(skus).then((res) => {
			if (cancelled) return;
			if (res.status === "success") {
				const inStock = res.products.filter((p) => p.inStock);
				setProducts(inStock);
			}
		});

		return () => {
			cancelled = true;
		};
	}, [skus]);

	if (products.length === 0) return null;

	return (
		<section className={styles["section"]}>
			<RecentlyViewedCarousel<SlideImageProps>
				title={{
					tag: "h2",
					textContent: title?.trim() || "Recently Viewed"
				}}
				addToCartLabel={tCart("addToCart")}
				viewLabel={tCart("view")}
				onAddToCart={dispatchAddToCart}
				products={products.slice(0, maxDisplay).map(mapToSlide)}
				embedded={embedded}
				allowOverflow={allowOverflow}
			/>
		</section>
	);
}
